(() => {
  'use strict';

  const STORAGE_KEY = 'zenkai:wishlist:v1';
  const memoryFallback = [];

  function readItems() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.id && item.handle) : [];
    } catch (_error) {
      return memoryFallback.slice();
    }
  }

  function writeItems(items) {
    const unique = Array.from(new Map(items.map((item) => [String(item.id), item])).values());
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
    } catch (_error) {
      memoryFallback.splice(0, memoryFallback.length, ...unique);
    }
    updateUi(unique);
    document.dispatchEvent(new CustomEvent('zenkai:wishlist:change', { detail: { items: unique } }));
  }

  function productFromButton(button) {
    return {
      id: String(button.dataset.productId || ''),
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || `/products/${button.dataset.productHandle || ''}`,
      image: button.dataset.productImage || '',
      price: Number(button.dataset.productPrice || 0),
      variantId: String(button.dataset.variantId || ''),
    };
  }

  function updateUi(items = readItems()) {
    const ids = new Set(items.map((item) => String(item.id)));
    document.querySelectorAll('[data-zenkai-wishlist-toggle]').forEach((button) => {
      const active = ids.has(String(button.dataset.productId));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const title = button.dataset.productTitle || 'Ürün';
      button.setAttribute('aria-label', `${title} ürününü istek listesinden ${active ? 'çıkar' : 'ekle'}`);
      const label = button.querySelector('span');
      if (label) label.textContent = active
        ? (button.dataset.labelRemove || 'İstek listesinden çıkar')
        : (button.dataset.labelAdd || 'İstek listesine ekle');
    });
    document.querySelectorAll('[data-zenkai-wishlist-count]').forEach((count) => {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    });
  }

  function toast(message) {
    document.querySelector('.zenkai-wishlist-toast')?.remove();
    const element = document.createElement('div');
    element.className = 'zenkai-wishlist-toast';
    element.setAttribute('role', 'status');
    element.textContent = message;
    document.body.appendChild(element);
    window.setTimeout(() => element.classList.add('is-leaving'), 1800);
    window.setTimeout(() => element.remove(), 2100);
  }

  function toggle(button) {
    const product = productFromButton(button);
    if (!product.id || !product.handle) return;
    const items = readItems();
    const index = items.findIndex((item) => String(item.id) === product.id);
    if (index >= 0) {
      items.splice(index, 1);
      toast('Ürün istek listesinden çıkarıldı.');
    } else {
      items.unshift(product);
      toast('Ürün istek listesine eklendi.');
    }
    writeItems(items);
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
        style: 'currency', currency, minimumFractionDigits: 2,
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  }

  function createCard(item, product, currency) {
    const card = document.createElement('article');
    card.className = 'zenkai-wishlist-card';
    card.dataset.productId = item.id;
    const url = product?.url || item.url || `/products/${item.handle}`;
    const title = product?.title || item.title;
    const image = product?.featured_image || item.image;
    const price = product?.price ?? item.price;
    const availableVariant = product?.variants?.find((variant) => variant.available) || product?.variants?.[0];

    const media = document.createElement('div');
    media.className = 'zenkai-wishlist-card__media';
    const mediaLink = document.createElement('a');
    mediaLink.href = url;
    mediaLink.setAttribute('aria-label', title);
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = title;
      img.loading = 'lazy';
      mediaLink.appendChild(img);
    }
    media.appendChild(mediaLink);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'zenkai-wishlist-card__remove';
    remove.dataset.wishlistRemove = item.id;
    remove.setAttribute('aria-label', `${title} ürününü istek listesinden çıkar`);
    remove.textContent = '×';
    media.appendChild(remove);

    const body = document.createElement('div');
    body.className = 'zenkai-wishlist-card__body';
    const titleLink = document.createElement('a');
    titleLink.className = 'zenkai-wishlist-card__title';
    titleLink.href = url;
    titleLink.textContent = title;
    const priceElement = document.createElement('p');
    priceElement.className = 'zenkai-wishlist-card__price';
    priceElement.textContent = money(price, currency);
    const actions = document.createElement('div');
    actions.className = 'zenkai-wishlist-card__actions';
    const view = document.createElement('a');
    view.href = url;
    view.textContent = 'Ürünü incele';
    actions.appendChild(view);
    if (availableVariant?.available) {
      const add = document.createElement('button');
      add.type = 'button';
      add.dataset.wishlistAdd = String(availableVariant.id);
      add.textContent = 'Sepete ekle';
      actions.appendChild(add);
    }
    body.append(titleLink, priceElement, actions);
    card.append(media, body);
    return card;
  }

  async function renderPage() {
    const root = document.querySelector('[data-zenkai-wishlist-page]');
    if (!root) return;
    const grid = root.querySelector('[data-zenkai-wishlist-grid]');
    const empty = root.querySelector('[data-zenkai-wishlist-empty]');
    const label = root.querySelector('[data-zenkai-wishlist-page-count]');
    const currency = root.dataset.currency || 'TRY';
    const items = readItems();
    grid.replaceChildren();
    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
    if (label) label.textContent = `${items.length} ürün`;
    if (!items.length) return;

    const cards = await Promise.all(items.map(async (item) => {
      let product = null;
      try {
        const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) product = await response.json();
      } catch (_error) {
        product = null;
      }
      return createCard(item, product, currency);
    }));
    grid.append(...cards);
  }

  document.addEventListener('click', async (event) => {
    const toggleButton = event.target.closest('[data-zenkai-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      toggle(toggleButton);
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      const items = readItems().filter((item) => String(item.id) !== String(removeButton.dataset.wishlistRemove));
      writeItems(items);
      toast('Ürün istek listesinden çıkarıldı.');
      renderPage();
      return;
    }

    const addButton = event.target.closest('[data-wishlist-add]');
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = 'Ekleniyor…';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(addButton.dataset.wishlistAdd), quantity: 1 }] }),
        });
        if (!response.ok) throw new Error('Cart request failed');
        window.location.assign('/cart');
      } catch (_error) {
        addButton.disabled = false;
        addButton.textContent = 'Tekrar dene';
        toast('Ürün sepete eklenemedi.');
      }
    }
  });

  document.addEventListener('zenkai:wishlist:change', () => renderPage());
  document.addEventListener('shopify:section:load', () => { updateUi(); renderPage(); });
  window.addEventListener('storage', () => { updateUi(); renderPage(); });

  function init() {
    updateUi();
    renderPage();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
